import { AutoComplete, Button, Input, Loader } from 'ccs-ui-components';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AppDispatch, useSelector } from '../../../../../store/AppStore';
import { EXCEPTION_ACTION, STATUS_TYPES, AUTOCOMPLETE_FIELD } from '../../../../../constants';
import { fetchExceptionRequest } from '../../../../../store/actions/sagaActions/exceptions/ExceptionSearchSagaAction';
import { addException } from '../../../../../store/reducer/services/ExceptionsReducer';
import { isErrorCodeValid } from '../../../../../utilities/services/ServiceListUtils';
import { ExceptionActionDropdown } from '../ExceptionActionDropdown';
import { getActionsErrorMessage } from '../exceptionUtils';

import classes from './ServiceExceptionForm.module.scss';

interface IServiceExceptionForm {
  isEditService: boolean;
  continueHandler: (status: boolean) => void;
}
export const ServiceExceptionForm = ({
  continueHandler,
  isEditService
}: IServiceExceptionForm) => {
  const { t: translate } = useTranslation();

  const { exceptions } = useSelector((state) => state.exceptionReducer);
  const { exceptions: exceptionList, isLoading } = useSelector(
    (state) => state.fetchExceptionReducer
  );
  const { serviceExceptionList, isLoading: isExceptionListLoading } = useSelector((state) => state.getServiceExceptionList);

  const dispatch = useDispatch<AppDispatch>();
  const [errorCode, setErrorCode] = useState<number>(0);
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(true);
  // State to check user has selected a valid exception name
  const [errorCodeError, setErrorCodeError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [clearData, setClearData] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedValue, setSelectedValue] = useState('');
  // Multiselect states
  const [action, setAction] = useState<string[]>([]);

  const handleData = (args: string[]) => {
    if (args.length < 1) setIsValid(true);
    setAction(args);
  };

  const HELPER_TEXT = {
    exception_ht: translate('helper_text.txt_exception_ht')
  };
  const dropDownValue = [
    {
      label: translate('service_exceptions.txt_notify'),
      value: 'Notify'
    },
    {
      label: translate('service_exceptions.txt_compensate'),
      value: 'Compensate'
    },
    {
      label: translate('service_exceptions.txt_ignore'),
      value: 'Ignore'
    },
    {
      label: translate('service_exceptions.txt_compensate_and_recover'),
      value: 'CompensateAndRecover'
    }
  ];

  const SERVICE_EXCEPTIONS_ERRORS = {
    duplicateCombinationError: translate(
      'service_exceptions.duplicateCombinationError'
    ),
    errorCodeValue: translate('service_exceptions.errorCodeValue'),
    wrongCombination: translate('service_exceptions.wrongCombination'),
    duplicateErrorCode: translate('service_exceptions.duplicateErrorCode'),
    duplicateExceptionName: translate(
      'service_exceptions.duplicateExceptionName'
    ),
    emptyExceptionSearch: translate('service_exceptions.emptyExceptionSearch')
  };

  /**
   * Checks for same exception name added previously
   * @returns true if exists, else false
   */
  const uniqueExceptionNameChecker = () => {
    const obj = exceptions.filter((item) => item.exceptionId === inputValue);

    if (!obj.length) {
      setError('');
      return true;
    }
    setError(SERVICE_EXCEPTIONS_ERRORS.duplicateExceptionName);
    return false;
  };

  /**
   * Checks for same exception error added previously
   * @returns true if exists, else false
   */
  const uniqueErrorChecker = () => {
    const obj = exceptions.filter((item) => item.errorCode === errorCode);

    if (!obj.length) {
      setErrorCodeError('');
      return true;
    }
    setErrorCodeError(SERVICE_EXCEPTIONS_ERRORS.duplicateErrorCode);
    return false;
  };

  /**
   * Checks for Compensate/CompensateAndRecover and Ignore not selected together
   * Also checks that Compensate and CompensateAndRecover are not selected together
   * @returns true if valid, else false
   */
  const uniqueActionChecker = () => {
    const actionArray = [...action];
    if (
      (actionArray.includes(EXCEPTION_ACTION.COMPENSATE)
        || actionArray.includes(EXCEPTION_ACTION.COMPENSATE_AND_RECOVER)
        || actionArray.includes(EXCEPTION_ACTION.NOTIFY))
      && actionArray.includes(EXCEPTION_ACTION.IGNORE)
    ) {
      const conflicting = [
        EXCEPTION_ACTION.IGNORE,
        ...actionArray.filter(
          (a) =>
            a === EXCEPTION_ACTION.COMPENSATE
            || a === EXCEPTION_ACTION.COMPENSATE_AND_RECOVER
            || a === EXCEPTION_ACTION.NOTIFY
        )
      ];
      setActionError(getActionsErrorMessage(conflicting, dropDownValue, translate));
      return false;
    }
    if (
      actionArray.includes(EXCEPTION_ACTION.COMPENSATE)
      && actionArray.includes(EXCEPTION_ACTION.COMPENSATE_AND_RECOVER)
    ) {
      setActionError(
        getActionsErrorMessage([
          EXCEPTION_ACTION.COMPENSATE,
          EXCEPTION_ACTION.COMPENSATE_AND_RECOVER
        ], dropDownValue, translate)
      );
      return false;
    }
    setActionError('');
    return true;
  };

  const errorCodeChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorCode(parseInt(e.target.value, 10));
  };

  const onAddHandler = () => {
    let id = 1;
    if (exceptions.length > 0) {
      id = exceptions[0].id + 1;
    }
    const exceptionBody = {
      exceptionId: inputValue,
      errorCode,
      action,
      isEdit: false,
      id
    };
    dispatch(addException(exceptionBody));
    setIsValid(true);
    setSelectedValue('');
    setSuggestions([]);
    setInputValue('');
    setClearData(true);
    setErrorCode(0);
    setAction([]);
  };

  // uplifting
  const handleInputChange = (e: string) => {
    if (inputValue.length < 1) setClearData(false);
    setInputValue(e);
  };

  // selectedValue
  const handleSelect = (value: string) => {
    setSelectedValue(value);
    setInputValue(value);
    setSuggestions([]);
  };

  useEffect(() => {
    if (action.length > 0 && action[0]) {
      uniqueActionChecker();
    } else setActionError('');
  }, [action]);

  useEffect(() => {
    if (inputValue) {
      uniqueExceptionNameChecker();
    } else setError('');
  }, [inputValue]);

  useEffect(() => {
    if (errorCode) {
      if (isErrorCodeValid(errorCode)) {
        setErrorCodeError('');
        uniqueErrorChecker();
      } else {
        setErrorCodeError(SERVICE_EXCEPTIONS_ERRORS.errorCodeValue);
        setIsValid(true);
      }
    } else {
      setErrorCodeError('');
    }
  }, [errorCode]);

  useEffect(() => {
    if (inputValue || errorCode || action.length > 0) {
      continueHandler(true);
    } else continueHandler(false);
    if (
      inputValue
      && errorCode
      && action.length > 0
      && selectedValue
      && selectedValue === inputValue
      && uniqueActionChecker()
      && uniqueExceptionNameChecker()
      && isErrorCodeValid(errorCode)
      && uniqueErrorChecker()
    ) {
      setIsValid(false);
    } else setIsValid(true);
  }, [
    inputValue,
    errorCode,
    action,
    exceptions,
    selectedValue,
    serviceExceptionList
  ]);

  // passing the exception name
  useEffect(() => {
    if (exceptionList.length > 0) {
      const exceptionNames = exceptionList.map((item) => item.ersExceptionName);
      setSuggestions(exceptionNames);
    } else if (inputValue.length > 0 && !isLoading) {
      setSuggestions([]);
      setError(SERVICE_EXCEPTIONS_ERRORS.emptyExceptionSearch);
    }
  }, [exceptionList]);

  // whenever the input Value changes
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (inputValue.length > 2) {
      timer = setTimeout(() => {
        dispatch(fetchExceptionRequest(inputValue, STATUS_TYPES.ACTIVE));
      }, 300);
    } else {
      setSuggestions([]);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [inputValue]);

  return (
    <div className={classes['exception-list']}>
      <Form
        onSubmit={(event) => {
          event.preventDefault();
        }}
        data-testid="exc-form"
      >
        <Loader show={isExceptionListLoading} />
        <fieldset disabled={!isEditService}>
          <Row className={classes['list-body']}>
            <Col sm={4}>
              <div className={classes['exception-form-input']}>
                <AutoComplete
                  suggestions={suggestions}
                  onSelect={handleSelect}
                  clearData={clearData}
                  onqueryUpdate={handleInputChange}
                  isLoading={isLoading}
                  placeHolder={translate(
                    'service_exceptions.search_exception_name'
                  )}
                  helperText={HELPER_TEXT.exception_ht}
                  fieldName={AUTOCOMPLETE_FIELD.exceptionName}
                  error={!!error.length}
                  errorTxt={error}
                />
              </div>
            </Col>
            <Col sm={3}>
              <Input
                label=""
                name="exception-error-code"
                onChange={errorCodeChangeHandler}
                value={errorCode || ''}
                data-testid="exception-error"
                placeholder={String(
                  translate('service_exceptions.plchd_error_code')
                )}
                className={classes['exception-form-input-error']}
                error={errorCodeError ? errorCodeError?.length > 0 : false}
                errorTxt={errorCodeError || ''}
              />
            </Col>
            <Col sm={3}>
              <div className={classes['status-dropdown']}>
                <ExceptionActionDropdown
                  options={dropDownValue}
                  onSelect={handleData}
                  selectedOptions={action}
                  height="38px"
                  data-testid="exception-action"
                  placeHolder={String(
                    translate('service_exceptions.plchd_action')
                  )}
                />
              </div>
              {actionError && (
                <div className={classes['error-container']}>{actionError}</div>
              )}
            </Col>
            <Col sm={2}>
              <span className={classes['btn-container']}>
                <Button
                  variant="secondary"
                  className={classes['add-button']}
                  disabled={isValid}
                  onClick={() => onAddHandler()}
                  data-testid="add-btn"
                >
                  {translate('service_exceptions.add_button')}
                </Button>
              </span>
            </Col>
          </Row>
        </fieldset>
      </Form>
    </div>
  );
};
